import type { ColorAnalysisOptions } from '@halo/utils';
import { ColorAnalyzer } from '@halo/utils';
import { useEffect, useState, useCallback } from 'react';
import { useRefs } from '.';

type ValueType = string | File | HTMLImageElement | undefined;
function detectImageType(value: ValueType) {
  if (value instanceof HTMLImageElement) {
    return 'fromImage';
  }

  if (value instanceof File && value.type.startsWith('image/')) {
    return 'fromFile';
  }

  if (typeof value === 'string' && value.startsWith('data:image/')) {
    return 'fromBase64';
  }

  if (typeof value === 'string' && value) {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    try {
      const url = new URL(value);
      const pathname = url.pathname.toLowerCase();
      const hasImageExt = imageExtensions.some((ext) => pathname.endsWith(ext));
      if (hasImageExt) return 'fromUrl';
    } catch (_) {
      console.warn('Not a valid URL' + value);
    }
  }

  return null;
}
export function useColorAnalyzer(value: ValueType, options: ColorAnalysisOptions = {}) {
  const { defaultColor = '' } = options;
  const [bgColor, setBgColor] = useState(defaultColor);

  const handleBase64Analysis = useCallback(async () => {
    const type = detectImageType(value);
    if (!value || !type) {
      return;
    }
    const analyzer = new ColorAnalyzer();
    try {
      const bgColor = await analyzer[type](value as any, options);
      setBgColor(bgColor as string);
    } catch (error) {
      console.error(error);
    }
  }, [value, options]);

  const refs = useRefs({
    handleBase64Analysis,
  });

  useEffect(() => {
    const { handleBase64Analysis } = refs.current;
    handleBase64Analysis();
  }, [refs, value]);

  return bgColor;
}
